import { useMemo, useState } from 'react';
import Map, { Layer, Source, MapLayerMouseEvent } from 'react-map-gl/maplibre';
import type { FilterSpecification, StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { PlatformStateMapData, PlatformStateProperties } from '../api/platform';

/**
 * The national opt-in map on the SGOKit marketing homepage, in SGOKit's own
 * palette (navy / gold / paper — sgokit.css). Deliberately not StateMap: that
 * component draws one tenant's footprint in the tenant's theme; this draws the
 * platform roster, where a state is simply opted in or not.
 *
 * Same constraints as StateMap, for the same reasons: no basemap (no tile
 * server or key), and so no labels — a hover caption names the state instead.
 * Fill colors are duplicated from sgokit.css because WebGL paint properties
 * can't read CSS custom properties.
 */
const EMPTY_STYLE: StyleSpecification = { version: 8, sources: {}, layers: [] };

// sgokit.css :root — --gold, --gold-600, --gold-200, --paper-2, --line-2, --navy.
const COLOR = {
  optedIn: '#ddab2b',
  optedInHover: '#c2941f',
  optedInOutline: '#c2941f',
  notYet: '#efe4d2',
  notYetHover: '#f0dca0',
  outline: '#d8cbb0',
};

const OPTED_IN = ['get', 'opted_in'] as FilterSpecification;
const NOT_YET = ['!', ['get', 'opted_in']] as FilterSpecification;

type Props = { data: PlatformStateMapData };

export default function PlatformStateMap({ data }: Props) {
  const [hovered, setHovered] = useState<PlatformStateProperties | null>(null);
  const optedInCount = useMemo(
    () => data.features.filter((f) => f.properties.opted_in).length,
    [data],
  );

  const handleHover = (event: MapLayerMouseEvent | null) => {
    const next = (event?.features?.[0]?.properties as unknown as PlatformStateProperties) ?? null;
    setHovered((prev) => (prev?.abbreviated_name === next?.abbreviated_name ? prev : next));
  };

  return (
    <div className="sgk-statewrap">
      <div
        className="sgk-statemap"
        role="img"
        aria-label={`United States map: ${optedInCount} states have opted in to the Section 25F credit.`}
        data-testid="platform-state-map"
      >
        <Map
          initialViewState={{ bounds: data.bbox, fitBoundsOptions: { padding: 8 } }}
          mapStyle={EMPTY_STYLE}
          scrollZoom={false}
          dragPan={false}
          dragRotate={false}
          doubleClickZoom={false}
          touchZoomRotate={false}
          keyboard={false}
          attributionControl={false}
          interactiveLayerIds={['platform-not-yet', 'platform-opted-in']}
          onMouseMove={handleHover}
          onMouseOut={() => handleHover(null)}
          style={{ width: '100%', height: '100%' }}
        >
          <Source id="platform-states" type="geojson" data={data}>
            <Layer id="platform-not-yet" type="fill" filter={NOT_YET} paint={{ 'fill-color': COLOR.notYet }} />
            <Layer id="platform-opted-in" type="fill" filter={OPTED_IN} paint={{ 'fill-color': COLOR.optedIn }} />
            <Layer
              id="platform-hover"
              type="fill"
              filter={['==', ['get', 'abbreviated_name'], hovered?.abbreviated_name ?? ''] as FilterSpecification}
              paint={{
                'fill-color': ['case', ['get', 'opted_in'], COLOR.optedInHover, COLOR.notYetHover],
              }}
            />
            <Layer id="platform-outline" type="line" paint={{ 'line-color': COLOR.outline, 'line-width': 0.75 }} />
            <Layer
              id="platform-opted-in-outline"
              type="line"
              filter={OPTED_IN}
              paint={{ 'line-color': COLOR.optedInOutline, 'line-width': 1 }}
            />
          </Source>
        </Map>
      </div>

      <div className="sgk-statemap__caption" aria-live="polite" data-testid="platform-state-map-caption">
        {hovered
          ? `${hovered.full_name} — ${hovered.opted_in ? 'opted in' : 'not yet opted in'}`
          : 'Hover a state'}
      </div>

      <div className="sgk-legend">
        <span><i className="in" />Opted in · {optedInCount}</span>
        <span><i className="out" />Not opted in</span>
      </div>
    </div>
  );
}
